import type { PhotoSessionSection } from "./photoSessionUtils";
import type { PhotoCarGuide } from "./carGuideTypes";
import { EXTERIOR_GUIDED_COUNT } from "./exteriorCarGuides";
import { INTERIOR_GUIDED_COUNT } from "./interiorCarGuides";

export interface PhotoSessionGuideLabelsInput {
  section: PhotoSessionSection;
  sectionTitle: string;
  nextPhotoIndex: number;
  maxForSection: number;
  activeGuide: PhotoCarGuide | null;
  exteriorGuides?: Record<string, string>;
  interiorGuides?: Record<string, string>;
  exteriorGuideStep?: string;
  interiorGuideStep?: string;
  guidedOptionalHint?: string;
  exteriorOptionalHint?: string;
  guidedOptionalSub?: string;
  exteriorOptionalSub?: string;
  sectionShotTitle?: string;
  landscapeGuide: string;
  landscapeFormatHint: string;
}

function guidedCountForSection(section: PhotoSessionSection): number {
  if (section === "exterior") return EXTERIOR_GUIDED_COUNT;
  if (section === "interior") return INTERIOR_GUIDED_COUNT;
  return 0;
}

function guidesMapForSection(
  section: PhotoSessionSection,
  exteriorGuides?: Record<string, string>,
  interiorGuides?: Record<string, string>,
): Record<string, string> | undefined {
  if (section === "exterior") return exteriorGuides;
  if (section === "interior") return interiorGuides;
  return undefined;
}

export function resolvePhotoSessionGuideLabels(
  input: PhotoSessionGuideLabelsInput,
): {
  floatingTitle: string;
  landscapeGuide: string;
  landscapeFormatHint: string;
} {
  const {
    section,
    sectionTitle,
    nextPhotoIndex,
    maxForSection,
    activeGuide,
    exteriorGuides,
    interiorGuides,
    exteriorGuideStep,
    interiorGuideStep,
    guidedOptionalHint,
    exteriorOptionalHint,
    guidedOptionalSub,
    exteriorOptionalSub,
    sectionShotTitle,
    landscapeGuide,
    landscapeFormatHint,
  } = input;

  const guidedCount = guidedCountForSection(section);
  const guidesMap = guidesMapForSection(
    section,
    exteriorGuides,
    interiorGuides,
  );
  const optionalHint =
    guidedOptionalHint ?? exteriorOptionalHint ?? "Optional angle";
  const optionalSub = guidedOptionalSub ?? exteriorOptionalSub ?? "";
  const stepTemplate =
    section === "exterior"
      ? exteriorGuideStep
      : section === "interior"
        ? interiorGuideStep
        : undefined;

  const formatSectionShot = (shot: string) =>
    sectionShotTitle
      ?.replace("{section}", sectionTitle)
      .replace("{shot}", shot) ?? `${sectionTitle} – ${shot}`;

  if (
    (section === "exterior" || section === "interior") &&
    activeGuide &&
    guidesMap
  ) {
    const shotName = guidesMap[activeGuide.labelKey] ?? activeGuide.labelKey;
    const stepHint =
      stepTemplate
        ?.replace("{step}", String(nextPhotoIndex))
        ?.replace("{max}", String(guidedCount)) ??
      `${nextPhotoIndex} / ${guidedCount}`;
    return {
      floatingTitle: formatSectionShot(shotName),
      landscapeGuide: shotName,
      landscapeFormatHint: stepHint,
    };
  }

  if (
    (section === "exterior" || section === "interior") &&
    nextPhotoIndex > guidedCount
  ) {
    return {
      floatingTitle: formatSectionShot(optionalHint),
      landscapeGuide: optionalHint,
      landscapeFormatHint: optionalSub
        .replace("{current}", String(nextPhotoIndex))
        .replace("{max}", String(maxForSection)),
    };
  }

  return {
    floatingTitle: sectionTitle,
    landscapeGuide,
    landscapeFormatHint,
  };
}
